import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, LayoutGrid, ClipboardList, Users,
  GraduationCap, Tag, Quote, Settings, Store, LogOut,
  TrendingUp, PackageCheck, ScrollText, BarChart3, Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/api";
import { AdminGate } from "@/components/AdminGate";

const NAV = [
  { to: "", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/products", label: "Products", icon: Package },
  { to: "/categories", label: "Categories", icon: LayoutGrid },
  { to: "/orders", label: "Orders", icon: ClipboardList },
  { to: "/outstanding", label: "Outstanding", icon: PackageCheck },
  { to: "/sales", label: "Sales", icon: TrendingUp },
  { to: "/visitors", label: "Visitors", icon: BarChart3 },
  { to: "/stokvel", label: "Stokvel", icon: Wallet },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/activity", label: "Activity", icon: ScrollText },
  { to: "/schools", label: "Schools & Lists", icon: GraduationCap },
  { to: "/promotions", label: "Promotions", icon: Tag },
  { to: "/quotations", label: "Quotations", icon: Quote },
  { to: "/settings", label: "Settings", icon: Settings },
];

function AdminConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The console is mounted at BOTH /admin and /admin-login — keep nav on
  // whichever base address the admin is using.
  const base = location.pathname.toLowerCase().startsWith("/admin-login") ? "/admin-login" : "/admin";

  return (
    <div className="min-h-screen bg-cream-light flex" data-testid="admin-layout">
      <aside className="w-16 lg:w-64 shrink-0 bg-navy-dark text-white flex flex-col sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="px-3 lg:px-5 py-5 border-b border-white/10">
          <span className="bg-white rounded-lg px-2 py-1.5 inline-flex">
            <img src={LOGO} alt="BTMEDU" className="h-7 w-auto" />
          </span>
          <p className="hidden lg:block text-[10px] uppercase tracking-[0.2em] text-gold font-bold mt-3">
            Admin Console
          </p>
        </div>
        <nav className="flex-1 py-4 px-2 lg:px-3 space-y-1 overflow-y-auto" data-testid="admin-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={base + n.to}
              end={n.end}
              title={n.label}
              className={({ isActive }) =>
                `flex flex-col lg:flex-row items-center lg:items-center gap-1 lg:gap-3 px-1.5 lg:px-3 py-2.5 rounded-xl font-semibold transition-colors ${
                  isActive ? "bg-gold text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <n.icon size={17} className="shrink-0" />
              <span className="text-[9px] lg:text-sm leading-tight text-center lg:text-left">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-2 lg:px-3 py-4 border-t border-white/10 space-y-1">
          <Link
            to="/"
            className="flex flex-col lg:flex-row items-center gap-1 lg:gap-3 px-1.5 lg:px-3 py-2.5 rounded-xl font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            data-testid="admin-view-store"
          >
            <Store size={17} className="shrink-0" />
            <span className="text-[9px] lg:text-sm leading-tight text-center lg:text-left">View store</span>
          </Link>
          <button
            onClick={async () => {
              await logout();
              navigate(base);
            }}
            className="w-full flex flex-col lg:flex-row items-center gap-1 lg:gap-3 px-1.5 lg:px-3 py-2.5 rounded-xl font-semibold text-white/70 hover:bg-red-500/15 hover:text-red-300 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={17} className="shrink-0" />
            <span className="text-[9px] lg:text-sm leading-tight text-center lg:text-left">Sign out</span>
          </button>
          <p className="hidden lg:block px-3 pt-2 text-[10px] text-white/30 truncate">{user?.email}</p>
        </div>
      </aside>
      <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-8">
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3.5 flex items-center gap-3" data-testid="live-changes-banner">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          <p className="text-xs sm:text-sm text-emerald-800">
            <span className="font-bold">Everything is live.</span> Any change you save here is published to the website instantly — no publish button needed.
          </p>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export default function AdminPage() {
  return (
    <AdminGate>
      <AdminConsole />
    </AdminGate>
  );
}
